// Sorten-Kacheln: die Dose oeffnet sich — Desktop beim Hover, iPhone beim Tipp.
//
// Eine kurze Animation: die Dose kippt, der Deckel geht auf, die Beutel
// fliegen heraus, am Ende steht sie wieder frontal da. Letzter Frame gleich
// erster Frame — deshalb gibt es an keiner Naht einen Sprung.
//
// DER MECHANISMUS IST VON SESH ABGESCHAUT (seshproducts.com, am 2026-08-03
// dekompiliert: custom-global-scripts.js + media.js, zusaetzlich am Live-DOM
// nachgemessen), weil er dort nachweislich auf dem iPhone funktioniert —
// waehrend zwei eigene Touch-Konstruktionen gescheitert sind (Details in der
// Git-Historie dieser Datei). Die drei Zutaten, alle drei uebernommen:
//
//   1. VORLADEN MIT WARM-UP. Quellen stehen frueh am Element, und ein kurzer
//      play()+pause()-Anlauf zwingt iOS, den Puffer wirklich zu fuellen —
//      preload="auto" allein holt dort nur Metadaten. Genau am leeren Puffer
//      starben beide eigenen Anlaeufe: ein play() ohne Daten wird abgelehnt
//      und loest auch keinen Download aus. (Sesh laedt ab Seitenaufbau in
//      1080p; wir hoeflicher erst, wenn die Kacheln in die Naehe des
//      Sichtfensters scrollen, und in 702px.)
//   2. TOUCHSTART SPIELT SOFORT, im Handler selbst, und der erste Klick wird
//      in der Capture-Phase geschluckt: Tipp eins zeigt die Animation statt
//      zu navigieren, Tipp zwei folgt dem Link.
//   3. MAUS-EREIGNISSE fuer den Desktop-Hover — die iOS beim Tippen
//      synthetisiert mitbedient: das synthetische mouseleave beim Tipp auf
//      eine andere Stelle raeumt die Karte auf.
//
// Waehrend eines Scrolls mit aufliegendem Finger laeuft die beruehrte Karte
// ebenfalls an (touchstart feuert am Beginn jeder Beruehrung) — zusaetzlich
// traegt dort das Standbild der geoeffneten Dose (lucid-is-open aus
// assets/lucid-scroller-open.js, rein per CSS) als Boden unter dem Video.
//
// DAS STANDBILD BLEIBT DIE ZWEITE EBENE DARUNTER: laeuft das Video nicht
// (noch nicht geladen, Ladefehler, Stromsparmodus), blendet nach
// STILL_DELAY_MS die geoeffnete Dose als Bild auf. Einen leeren Zustand gibt
// es nie. KEIN poster-Attribut, bewusst: das Kachelbild darunter ist Frame 1
// der Animation (Abweichung ~2 von 255, bei Kartengroesse unsichtbar).
//
// Kosten des Vorladens: vier MP4s zu je ~400 KB, aber erst, wenn jemand bis
// zu den Sorten scrollt, und nie bei prefers-reduced-motion. Das ist der
// Preis dafuer, dass die Animation im Moment der Beruehrung wirklich laeuft —
// Sesh zahlt ihn ab Seitenaufbau und in 1080p, wir gezielter. Der fruehere
// Geiz (laden erst beim Hover) war auf Desktop als Flackern sichtbar:
// Standbild kurz, dann Video. Mit Vorlauf gibt es diesen Zwischenzustand
// nicht mehr.

(() => {
  // Wer Bewegung abbestellt hat, bekommt weder Video noch Vorladen. Die CSS
  // blendet zusaetzlich aus, falls doch ein Element im DOM landet.
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  // So lange wartet das Standbild, bevor es aufblendet — die Rueckfallebene,
  // falls das Video beim ersten Kontakt doch noch nicht spielbereit ist.
  const STILL_DELAY_MS = 350;

  // Ab hier hat das Standbild uebernommen. Ein Video, das erst jetzt kommt,
  // wuerde die Dose sichtbar wieder zuklappen — es bleibt fuer diesen Kontakt
  // unsichtbar und ist beim naechsten sofort da. 2,5s statt der frueheren
  // 900ms: auf echten iPhones vergehen zwischen Tipp und erstem `playing`
  // auch bei warmem Puffer schon mal ueber eine Sekunde (AVPlayer-Anlauf),
  // und Franz' Prioritaet ist, dass die Animation kommt — nicht, dass der
  // Uebergang in jedem Randfall fugenlos ist.
  const LATE_MS = 2500;

  // Die URL steht als Custom Property an der Kachel (lucid-brand.css). Ein
  // url() in einer Variablen laedt nichts — erst das src hier tut das.
  //
  // Das abschliessende Ent-Escaping ist DER iOS-Fix (2026-09-01, im
  // iPhone-Simulator auf echtem WebKit gefangen): Safari serialisiert die
  // Custom Property als url(\/cdn\/shop\/files\/lucid_can_mint\.mp4) —
  // jedes / und . per Backslash escaped, Chromium tut das nicht. Ohne das
  // Ent-Escaping standen die Backslashes woertlich im src, der Server
  // antwortete mit Muell, und WebKit meldete NotSupportedError mit
  // networkState 3. Deshalb scheiterte JEDE Mechanik-Variante nur auf
  // iPhones: alle lasen die URL ueber diese Funktion, waehrend das
  // CSS-Standbild (background-image: var(...)) nie durch JS musste.
  const urlFrom = (styles, name) => {
    const raw = styles.getPropertyValue(name).trim();
    if (!raw || raw === 'none') return '';
    return raw
      .replace(/^url\(\s*["']?/, '')
      .replace(/["']?\s*\)$/, '')
      .replace(/\\(.)/g, '$1');
  };

  // Nur MP4, kein WebM: Shopify liefert die hochgeladenen .webm-Dateien mit
  // `content-type: audio/webm` aus (gemessen am CDN) — Safari verweigert die.
  // Die .webm-Dateien liegen weiter in Shopify Files und die CSS-Variable
  // --lucid-can-webm bleibt bestehen, benutzt wird sie nicht.
  const build = (block) => {
    const styles = getComputedStyle(block);
    const mp4 = urlFrom(styles, '--lucid-can-mp4');

    // Sorte ohne hinterlegte Animation: Kachel bleibt, wie sie ist.
    if (!mp4) return null;

    const video = document.createElement('video');
    video.className = 'lucid-can-video';
    video.muted = true;
    // defaultMuted setzt das ATTRIBUT — ohne das behandelt Safari den Clip als
    // tonhaltig und verweigert autoplay bzw. play() ohne Nutzergeste. Das
    // setAttribute zusaetzlich, weil Sesh es so macht und iOS-Versionen beim
    // Reflektieren von defaultMuted unterschiedlich zuverlaessig sind.
    video.defaultMuted = true;
    video.setAttribute('muted', '');
    video.playsInline = true;
    video.preload = 'none';
    video.setAttribute('playsinline', '');
    // Aeltere WebKit-Schreibweise, kostet nichts — Sesh setzt sie ebenfalls.
    video.setAttribute('webkit-playsinline', '');
    video.setAttribute('disableremoteplayback', '');
    // Dekoration: die Sorte steht als Text daneben, der Screenreader braucht
    // die drehende Dose nicht.
    video.setAttribute('aria-hidden', 'true');
    video.tabIndex = -1;
    // Bewusst ohne loop: das ist eine Bewegung, keine Schleife. Nach `ended`
    // bleibt der letzte Frame stehen — er zeigt dasselbe wie das Kachelbild.
    block.appendChild(video);

    let primed = false;
    let engaged = false;
    let engagedAt = 0;
    let shown = false;
    let failed = false;

    // Ladefehler: Video aufgeben, Standbild traegt. Mit direktem src feuert
    // das error-Ereignis am Element selbst (anders als bei <source>-Kindern,
    // deren Fehler dort nie ankamen).
    const fail = () => {
      engaged = false;
      primed = false;
      shown = false;
      failed = true;
      hide();
      video.removeAttribute('src');
      video.preload = 'none';
      video.load();
    };

    // Das Vorladen: src setzen, laden — und einmal kurz anspielen und sofort
    // wieder pausieren. Dieser Warm-up-Zug stammt woertlich aus dem
    // Sesh-Skript und ist KEIN Zierrat: iOS behandelt preload="auto"
    // bestenfalls als Empfehlung und holt ohne play() nur Metadaten. Erst der
    // Play-Anlauf fuellt den Puffer wirklich. Stumm + inline darf er ohne
    // Geste laufen; wo nicht (Stromsparmodus), schluckt der catch das
    // folgenlos — dann laedt eben der erste echte Tipp. Sichtbar wird dabei
    // nichts: show() verlangt `engaged`, und das setzt nur engage().
    //
    // src DIREKT am Element, keine <source>-Kinder: Safari wertet dynamisch
    // angehaengte <source>-Elemente notorisch unzuverlaessig aus — Sesh hat
    // seine Quellen fest im Server-HTML und war deshalb nie in dieser Falle.
    // Chromium verzeiht beides; alle drei gescheiterten iPhone-Anlaeufe
    // hatten die <source>-Variante gemeinsam.
    const prime = () => {
      if (primed) return;
      primed = true;
      failed = false;
      video.src = mp4;
      video.preload = 'auto';
      video.load();
      const warmup = video.play();
      if (warmup) {
        warmup
          .then(() => {
            // Inzwischen echt angefasst? Dann bloss nicht wieder anhalten.
            if (engaged) return;
            video.pause();
            video.currentTime = 0;
          })
          .catch(() => {
            // Abgelehntes Warm-up UND nichts gepuffert (Stromsparmodus laedt
            // ohne Geste auch nichts): den naechsten echten Kontakt einen
            // kompletten neuen Anlauf nehmen lassen, statt auf einem toten
            // Element zu beharren.
            if (video.readyState === 0) primed = false;
          });
      }
    };

    const show = () => {
      // Zu spaet: das Standbild steht schon offen da. Nicht mehr dazwischen-
      // funken, sondern still fertig laden — der naechste Kontakt sitzt.
      if (!engaged || Date.now() - engagedAt > LATE_MS) return;
      shown = true;
      video.dataset.lucidCanOn = '';
      block.style.setProperty('--lucid-can-still', '0');
    };

    const hide = () => {
      delete video.dataset.lucidCanOn;
      block.style.removeProperty('--lucid-can-still');
    };

    // Sesh setzt muted/playsInline unmittelbar vor JEDEM play() erneut —
    // uebernommen, weil es nichts kostet und eine ganze Klasse von
    // iOS-Zustandsfragen beerdigt.
    const tryPlay = () => {
      video.muted = true;
      video.playsInline = true;
      const started = video.play();
      if (started) started.catch(() => {});
    };

    // Solange die Animation noch nie sichtbar lief, darf jeder neue Kontakt
    // einen frischen Anlauf nehmen (neues LATE_MS-Fenster). Sonst saesse eine
    // Karte, deren erster Versuch zu langsam war, fuer immer in der Falle:
    // engaged bliebe true, show() bliebe gesperrt, und kein Tipp koennte je
    // wieder etwas ausloesen.
    const engage = () => {
      if (engaged && shown) return;
      engaged = true;
      engagedAt = Date.now();
      prime();
      tryPlay();
    };

    // Nachfassen ohne Zustandswechsel: derselbe play()-Aufruf noch einmal,
    // aus einem Handler mit echter Nutzer-Aktivierung (click). Braucht der
    // Stromsparmodus: dort wird das stumme play() aus touchstart/Warm-up
    // abgelehnt, das aus dem Klick nicht.
    const poke = () => {
      if (!engaged) return;
      tryPlay();
    };

    const release = () => {
      if (!engaged) return;
      engaged = false;
      shown = false;
      hide();
      video.pause();
      try {
        video.currentTime = 0;
      } catch (e) {
        /* Ohne geladene Daten lehnen aeltere Browser das ab. */
      }
      // src bleibt dran — er ist das Vorladen. Der naechste Kontakt spielt
      // aus dem Puffer, ohne Netz.
    };

    // Drei Ereignisse, weil keines allein ueberall kommt: `playing` ist das
    // ehrlichste (es laeuft wirklich), `loadeddata` faellt auf Browsern an,
    // die vor dem ersten Frame nicht `playing` melden, und `timeupdate` ist
    // der Beleg, dass die Zeit tatsaechlich laeuft. show() ist idempotent.
    video.addEventListener('playing', show);
    video.addEventListener('loadeddata', show);
    video.addEventListener('timeupdate', show);

    // Nach `ended` bleibt das Video sichtbar auf dem letzten Frame stehen —
    // der zeigt dasselbe wie das Kachelbild darunter (letzter Frame gleich
    // erster Frame). KEIN hide() hier: auf Desktop haelt der Hover den
    // Zustand womoeglich noch, und ein Zuruecksetzen der --lucid-can-still-
    // Variable wuerde dann das OFFEN-Standbild ueber die zugeklappte Dose
    // blenden — ein sichtbares Wiederaufgehen aus dem Nichts.

    // Dekodierfehler am Element selbst (nach erfolgreichem Laden) zaehlen
    // ebenfalls als Ausfall.
    video.addEventListener('error', () => {
      if (primed) fail();
    });

    return {
      prime,
      engage,
      release,
      poke,
      hasShown: () => shown,
      // Zeigbar heisst: laeuft schon, oder mindestens ein Frame ist
      // dekodierbar, oder ein frischer Anlauf ist unterwegs und noch nicht
      // gescheitert (iOS haengt bis zum echten Playback oft bei
      // readyState 1, im Stromsparmodus zwangslaeufig bei 0 — der
      // click-poke gleich danach traegt die Aktivierung, die es dort
      // braucht). Nach einem error-Event navigiert der Tipp wieder normal,
      // eine Karte darf nie wegen eines stummen Videos tot sein.
      isShowable: () => shown || video.readyState >= 2 || (engaged && !failed),
    };
  };

  const wire = (block) => {
    if (block.dataset.lucidCanVideo) return;
    const api = build(block);
    if (!api) return;
    block.dataset.lucidCanVideo = 'on';
    // Erst ab hier wartet das Standbild auf das Video (Rueckfall-Verzoegerung,
    // liest die CSS im Aufblend-Zustand). Ohne Video-Element bleibt die
    // Variable ungesetzt und das Standbild blendet sofort.
    block.style.setProperty('--lucid-can-still-delay', `${STILL_DELAY_MS}ms`);

    // Die ganze Karte, nicht nur das Bild. Maus-Ereignisse tragen den
    // Desktop-Hover — und auf iOS zusaetzlich das Aufraeumen: einen Tipp
    // woanders beantwortet iOS mit einem synthetischen mouseleave, das die
    // Karte zurueckblendet. (mouseenter statt pointerenter, weil der
    // Touch-Pointer beim Abheben sofort ein pointerleave nachwirft — der
    // Zustand soll aber kleben, bis woanders getippt wird.)
    const card = block.closest('.group-block') || block;
    card.addEventListener('mouseenter', api.engage);
    card.addEventListener('mouseleave', api.release);

    // Der Sesh-Mobilzweig: touchstart spielt SOFORT (im Handler selbst),
    // der erste Klick danach wird in der Capture-Phase geschluckt, damit
    // der Tipp nicht gleichzeitig dem Karten-Link folgt. Der zweite Tipp
    // trifft auf eine schon laufende Karte, wird nicht mehr abgefangen und
    // navigiert — Tipp eins zeigt die Animation, Tipp zwei fuehrt zur Sorte.
    //
    // Bewusste Abweichung von Sesh: KEIN preventDefault auf touchstart
    // (Sesh: {passive:false} + preventDefault). Ein abgebrochenes touchstart
    // unterbindet in Safari auch das Scrollen, das auf der Karte beginnt —
    // bei Sesh mag das durchgehen, bei uns soll die Seite unterm Daumen
    // rollen. Die Klick-Unterdrueckung uebernimmt der Capture-Handler; das
    // 800ms-Fenster begrenzt sie auf den Tipp, der die Karte gestartet hat
    // (ein Scroll-Start feuert kein click und laesst das Fenster verfallen).
    //
    // api.poke() im Klick-Handler ist der Stromsparmodus-Pfad: dort lehnt
    // iOS das stumme play() aus touchstart/Warm-up ab, akzeptiert aber das
    // aus einem Klick mit echter Nutzer-Aktivierung.
    // Massgeblich ist hasShown, nicht "schon angefasst": lief die Animation
    // noch nie SICHTBAR, nimmt jeder Tipp einen frischen Anlauf (und wird
    // geschluckt). Erst wenn sie einmal lief, navigiert der naechste Tipp.
    let armedUntil = 0;
    card.addEventListener(
      'touchstart',
      () => {
        if (api.hasShown()) return;
        armedUntil = Date.now() + 800;
        api.engage();
      },
      { passive: true }
    );
    // touchend zusaetzlich zu touchstart: laut webkit.org zaehlen fuer
    // play() nur Handler von touchend/click/keydown als Nutzer-Geste —
    // touchstart bewusst nicht. Ausserhalb des Stromsparmodus spielt schon
    // der touchstart (stumm+inline braucht keine Geste); IM Stromsparmodus
    // ist dieser touchend-Anlauf der erste, der durchkommt.
    card.addEventListener(
      'touchend',
      () => {
        if (api.hasShown()) return;
        armedUntil = Date.now() + 800;
        api.poke();
      },
      { passive: true }
    );
    card.addEventListener(
      'click',
      (event) => {
        if (Date.now() > armedUntil) return;
        armedUntil = 0;
        // Geschluckt wird nur, wenn es auch etwas zu sehen gibt: Video schon
        // sichtbar oder zumindest spielbereit (dann kommt das playing-Event
        // Millisekunden spaeter). Kann das Video nicht (kaputt, nicht
        // geladen, iOS launisch), navigiert der Tipp ganz normal — die Karte
        // darf niemals wegen eines stummen Videos tot sein. Sesh schluckt
        // bedingungslos und nimmt tote Karten in Kauf; wir nicht.
        if (!api.isShowable()) return;
        event.preventDefault();
        event.stopPropagation();
        api.poke();
      },
      true
    );

    return api;
  };

  // Kein :has() im Selektor: die Kachel-Erkennung soll auch dort noch greifen,
  // wo :has() fehlt. Gleiche Semantik wie `:has(> img[src*='lucid-can-'])`.
  const wireAll = (scope) => {
    const apis = [];
    for (const block of scope.querySelectorAll('.image-block')) {
      if (!block.querySelector(':scope > img[src*="lucid-can-"]')) continue;
      const api = wire(block);
      if (api) apis.push([block, api]);
    }
    if (!apis.length) return;

    // Das Vorladen, Kern des Sesh-Rezepts: sobald die Kacheln in die Naehe
    // des Sichtfensters kommen, laden alle vier Videos — dann ist play() im
    // Moment der Beruehrung nur noch ein Abspielen aus dem Puffer. Dieselbe
    // 400px-Vorlaufzone wie beim Standbild-Vorladen in lucid-scroller-open.js.
    if ('IntersectionObserver' in window) {
      const observer = new IntersectionObserver(
        (entries) => {
          if (!entries.some((entry) => entry.isIntersecting)) return;
          observer.disconnect();
          for (const [, api] of apis) api.prime();
        },
        { rootMargin: '400px 0px' }
      );
      observer.observe(apis[0][0]);

      // Auftakt beim Reinscrollen (Franz, 2026-09-01): steht die erste Kachel
      // halb im Sichtfeld, oeffnet sich EINE Dose von selbst — ein Anker, der
      // zeigt, dass die Kacheln leben. Bewusst nur die erste (vier auf einmal
      // waere Kirmes), bewusst nur einmal pro Seitenaufruf (bei jedem
      // Vorbeiscrollen erneut wuerde es sich abnutzen).
      //
      // Die halbe Sichtbarkeit plus 400ms Bedenkzeit filtern das schnelle
      // Durchscrollen: wer nur vorbeirauscht, hat die Kachel beim Timeout
      // schon wieder verlassen, und wer schon selbst eine Dose angefasst hat
      // (hasShown), bekommt keinen Auftakt mehr dazwischengefunkt.
      //
      // engage() statt eines eigenen Pfads: gleiche Sichtbarkeits-Logik,
      // gleiches Ende (letzter Frame = Kachelbild), und laeuft das stumme
      // play() ohne Geste nicht (Stromsparmodus), passiert schlicht nichts
      // Sichtbares — der catch in tryPlay traegt das.
      const first = apis[0];
      let kickedOff = false;
      const kickoff = new IntersectionObserver(
        (entries) => {
          const hit = entries.find((entry) => entry.isIntersecting);
          if (!hit || kickedOff) return;
          kickedOff = true;
          kickoff.disconnect();
          setTimeout(() => {
            const box = first[0].getBoundingClientRect();
            const stillVisible = box.top < innerHeight && box.bottom > 0;
            if (!stillVisible) return;
            if (apis.some(([, api]) => api.hasShown())) return;
            first[1].engage();
          }, 400);
        },
        { threshold: 0.5 }
      );
      kickoff.observe(first[0]);
    }
  };

  wireAll(document);
  // Der Theme-Editor rendert Sections neu; die neuen Kacheln brauchen ihr Video.
  document.addEventListener('shopify:section:load', (event) => wireAll(event.target));
  // Sicherheitsnetz gegen ein Wettrennen mit dem Stylesheet: laeuft dieses
  // defer-Skript, bevor lucid-brand.css fertig ist, liefert getComputedStyle
  // leere Variablen — build() gibt null zurueck und die Kachel bleibt
  // unmarkiert (im Simulator als Lauf mit "videos im DOM: 0" beobachtet).
  // Nach window.load ist jedes Stylesheet sicher da; wireAll ist dank der
  // dataset-Markierung idempotent und ruestet nur nach, was fehlte.
  window.addEventListener('load', () => wireAll(document), { once: true });
})();
